import { type RewardsServerRead, readRewards } from '../rewards-hub/client';
import { mapLeaderboard, toBoardDto, toDirectionDto, toMetricDto, toPeriodDto } from './mappers';
import { mockLeaderboard, REWARDS_LEADERBOARD_MOCK } from './mocks';
import {
  DEFAULT_LEADERBOARD_DIRECTION,
  type LeaderboardMetric,
  type LeaderboardPeriod,
  type LeaderboardSegment,
  type LeaderboardSortDirection,
  type RewardsLeaderboard,
} from './models';
import type { LeaderboardDto, LeaderboardQueryDto } from './types';

/**
 * Leaderboard of `gamification-service`, alongside the rest of the Rewards Hub,
 * read through the hub client — the browser's read and the server render's.
 */

export interface LeaderboardParams {
  segment: LeaderboardSegment;
  metric: LeaderboardMetric;
  period: LeaderboardPeriod;
  /** 1-based. */
  page: number;
  /** Best first when left out — the cards and the winners never ask otherwise. */
  direction?: LeaderboardSortDirection;
}

/**
 * No onboarding gate. The top 200 under the metric, a page of 20 at a time
 * (28.09), best or worst first (02.11). The ordering is the server's: the place
 * a row prints is its place in the full ranking, which a client-side sort would
 * falsify.
 */
export async function fetchRewardsLeaderboard(
  { segment, metric, period, page, direction = DEFAULT_LEADERBOARD_DIRECTION }: LeaderboardParams,
  server?: RewardsServerRead,
): Promise<RewardsLeaderboard> {
  if (REWARDS_LEADERBOARD_MOCK) {
    return mapLeaderboard(mockLeaderboard({ segment, metric, period, page, direction }));
  }
  const query: LeaderboardQueryDto = {
    board: toBoardDto(segment),
    metric: toMetricDto(metric),
    period: toPeriodDto(period),
    order: toDirectionDto(direction),
    page,
  };
  return mapLeaderboard(
    await readRewards<LeaderboardDto>('/leaderboard', server, { ...query }),
    period,
  );
}
